
import { useState, useEffect } from 'react';

export interface CartItem {
  id: string;
  name: string;
  option: string;
  price: number;
  quantity: number;
  image: string;
}

const CART_STORAGE_KEY = 'choidam-cart';

const getStoredCart = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveCart = (cart: CartItem[]) => {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
};

// 전역 상태 관리를 위한 이벤트
const cartEventTarget = new EventTarget();
const CART_UPDATE_EVENT = 'cart-update';

export const useCart = () => {
  const [cart, setCart] = useState<CartItem[]>(getStoredCart);

  useEffect(() => {
    const handleCartUpdate = () => {
      setCart(getStoredCart());
    };

    cartEventTarget.addEventListener(CART_UPDATE_EVENT, handleCartUpdate);
    return () => {
      cartEventTarget.removeEventListener(CART_UPDATE_EVENT, handleCartUpdate);
    };
  }, []);

  const addToCart = (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
    const currentCart = getStoredCart();
    const existingIndex = currentCart.findIndex(
      (cartItem) => cartItem.id === item.id && cartItem.option === item.option
    );

    let newCart: CartItem[];
    if (existingIndex > -1) {
      newCart = currentCart.map((cartItem, index) =>
        index === existingIndex
          ? { ...cartItem, quantity: cartItem.quantity + (item.quantity || 1) }
          : cartItem
      );
    } else {
      newCart = [...currentCart, { ...item, quantity: item.quantity || 1 }];
    }

    saveCart(newCart);
    setCart(newCart);
    cartEventTarget.dispatchEvent(new Event(CART_UPDATE_EVENT));
  };

  const removeFromCart = (id: string, option: string) => {
    const currentCart = getStoredCart();
    const newCart = currentCart.filter(
      (item) => !(item.id === id && item.option === option)
    );
    saveCart(newCart);
    setCart(newCart);
    cartEventTarget.dispatchEvent(new Event(CART_UPDATE_EVENT));
  };

  const updateQuantity = (id: string, option: string, quantity: number) => {
    if (quantity < 1) return;
    const currentCart = getStoredCart();
    const newCart = currentCart.map((item) =>
      item.id === id && item.option === option ? { ...item, quantity } : item
    );
    saveCart(newCart);
    setCart(newCart);
    cartEventTarget.dispatchEvent(new Event(CART_UPDATE_EVENT));
  };

  const clearCart = () => {
    saveCart([]);
    setCart([]);
    cartEventTarget.dispatchEvent(new Event(CART_UPDATE_EVENT));
  };

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice,
  };
};
